import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { Bell, Check, CheckCheck, PenTool, CalendarDays, CheckSquare, Share2, Shield, FileText, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  meeting_invite: CalendarDays,
  task_assigned: CheckSquare,
  document_shared: Share2,
  signing_request: PenTool,
  signing_complete: Check,
  security_alert: Shield,
  access_revoked: X,
  invitation: FileText,
};

const COLOR_MAP = {
  meeting_invite: 'text-blue-400',
  task_assigned: 'text-amber-400',
  document_shared: 'text-emerald-400',
  signing_request: 'text-purple-400',
  signing_complete: 'text-green-400',
  security_alert: 'text-red-400',
  access_revoked: 'text-red-400',
  invitation: 'text-cyan-400',
};

const NotificationBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  const fetchNotifications = useCallback(async () => {
  try {
    const res = await fetch(`${API_URL}/api/notifications?limit=20`, {
      credentials: 'include', 
      headers: authHeaders()
    });
    if (res.ok) {
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    }
  } catch (err) {
    console.error(err);
  }
}, [API_URL, authHeaders]); // <-- Add variables used inside the callback

  // Poll for notifications every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const markRead = async (notifId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notifId}/read`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === notifId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markRead(notif.notification_id);
    const data = notif.data || {};
    if (notif.type === 'meeting_invite' && data.meeting_id) navigate('/calendar');
    else if (notif.type === 'task_assigned' && data.task_id) navigate('/tasks');
    else if (notif.type === 'signing_request' && data.document_id) navigate('/documents');
    else if (notif.type === 'document_shared') navigate('/shared');
    setIsOpen(false);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        className="relative p-2 text-white/60 hover:text-white transition-colors rounded-sm hover:bg-white/5"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-[#0A0A0A] border border-white/10 rounded-md shadow-2xl overflow-hidden z-50"
          data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-white font-medium text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = ICON_MAP[notif.type] || Bell;
                const iconColor = COLOR_MAP[notif.type] || 'text-white/40';
                return (
                  <button
                    key={notif.notification_id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-white/5 transition-colors border-b border-white/5 ${!notif.read ? 'bg-primary/5' : ''}`}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    <div className={`mt-0.5 p-1.5 rounded-sm ${!notif.read ? 'bg-white/10' : 'bg-white/5'}`}>
                      <Icon className={`w-4 h-4 ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.read ? 'text-white font-medium' : 'text-white/60'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-white/25 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
